/**
 * Roraima's moriche palm, shared between the menu strip and the chat wallpaper.
 *
 * Roraima stands over the Gran Sabana and the trees below it are fan palms, not
 * conifers (owner 2026-08-13: "roraima dağı çevresindeki ağaçlardan olsun, çam
 * ağacı çizdin"). The strip plants whole palms in the savanna at the foot of the
 * range; the wallpaper drifts single fronds off them.
 *
 * That pairing is also what fixed the wallpaper. It used to scatter the theme's
 * tepui at every scale, and a mountain hanging in mid-air is exactly as odd as
 * it sounds (owner: "uçan dağlar saçma bir fikir"). A frond is a thing that
 * genuinely blows around, which is the same relationship Sakura has between its
 * blossom and its petals and Autumn between its tree and its leaves.
 *
 * Lives here because two components draw it, which is the rule copies always
 * break ([[line-shared-glyph-modules]]).
 */

const f = ( v: number ) => +v.toFixed( 1 );

/**
 * ONE frond as a filled blade, authored in the shared 120 box: base at the
 * bottom-left, arching up and to the right, drooping at the tip.
 *
 * Built rather than hand-written so the taper stays even. The blade is widest
 * a third of the way along and comes to a point, and the spine's droop past the
 * midpoint is what stops it reading as a feather.
 */
function frondBlade(): string {
  const N = 30;                                  // leaflet pairs along the blade
  const x0 = 16, y0 = 104, x1 = 108, y1 = 30;    // base -> tip
  const cx = 52, cy = 34;                        // control: arch up early
  const at = ( t: number ): [ number, number ] => {
    const u = 1 - t;
    return [
      u * u * x0 + 2 * u * t * cx + t * t * x1,
      u * u * y0 + 2 * u * t * cy + t * t * y1 + t * t * t * 26,   // tip droops
    ];
  };
  const top: string[] = [];
  const bot: string[] = [];
  for ( let i = 0; i <= N; i++ ) {
    const t = i / N;
    const [ x, y ] = at( t );
    const [ nx, ny ] = at( Math.min( 1, t + 0.01 ) );
    let dx = nx - x, dy = ny - y;
    const L = Math.hypot( dx, dy ) || 1;
    dx /= L; dy /= L;
    // Widest about a third along, pointed at both ends.
    const w = 16 * Math.pow( Math.sin( Math.PI * Math.pow( t, 0.72 ) ), 0.85 );
    // A frond is PINNATE — the edge is a run of leaflets, not a smooth curve.
    // Without these notches the blade came out as a plain crescent and read as a
    // banana; the teeth are the single thing that says palm at wallpaper size.
    const notch = i % 2 === 0 ? 1 : 0.46;
    top.push( `${ f( x - dy * w * notch ) },${ f( y + dx * w * notch ) }` );
    bot.push( `${ f( x + dy * w * ( i % 2 === 0 ? 0.46 : 1 ) ) },${ f( y - dx * w * ( i % 2 === 0 ? 0.46 : 1 ) ) }` );
  }
  return `M${ top[ 0 ] } L${ top.slice( 1 ).join( ' L' ) } L${ bot.reverse().join( ' L' ) } Z`;
}

/** The frond, in the 120 box. */
export const FROND_D = frondBlade();

/**
 * A whole palm for the strip: a leaning trunk under a crown of arching fronds,
 * returned as two paths so the caller can stroke them at different weights.
 * Drawn at `h` tall with its foot on (x, y).
 */
export function palmPaths( x: number, y: number, h: number, lean: number ): { trunk: string; fronds: string } {
  const topX = x + lean, topY = y - h;
  const trunk = `M${ f( x ) },${ f( y ) } Q${ f( x + lean * 0.35 ) },${ f( y - h * 0.55 ) } ${ f( topX ) },${ f( topY ) }`;
  const len = h * 0.52;
  const fronds = [ 200, 236, 270, 304, 340 ].map( deg => {
    const a = ( deg * Math.PI ) / 180;
    // The tip droops below the line it set out on, which is the whole
    // silhouette of a palm; without it these are just spokes.
    const ex = topX + Math.cos( a ) * len;
    const ey = topY + Math.sin( a ) * len + len * 0.34;
    const mx = topX + Math.cos( a ) * len * 0.55;
    const my = topY + Math.sin( a ) * len * 0.55 - len * 0.16;
    return `M${ f( topX ) },${ f( topY ) } Q${ f( mx ) },${ f( my ) } ${ f( ex ) },${ f( ey ) }`;
  } ).join( ' ' );
  return { trunk, fronds };
}

/**
 * THE FAN FROND — the moriche's actual leaf, and the chat wallpaper's fragment
 * (owner 2026-08-14, after the gull was reassigned to Istanbul).
 *
 * Botanically this is the correction that matters. `frondBlade` above is drawn
 * PINNATE, an arching blade with leaflets running down a spine, which is a
 * coconut's leaf and Miami's. Mauritia flexuosa is PALMATE: every leaflet
 * springs from ONE point at the top of the stalk and the whole thing opens as a
 * fan. Two different silhouettes, which is exactly what keeps this wallpaper
 * from reading as Miami's.
 *
 * Three details are load-bearing and must not be "tidied":
 *  - the OUTER leaflets are shorter than the middle ones and fall away further.
 *    Equal-length leaflets read as a sunburst, not a leaf.
 *  - the stalk. Without it a fan has no root and tumbles as an abstract shape.
 *  - the leaflets are separate closed paths meeting at the hinge, not one
 *    notched outline. At wallpaper scale the gaps between them are what say palm.
 */
function fanFrond( k: number ): string {
  const fr = ( v: number ) => v - Math.floor( v );
  const n = 11 + Math.round( fr( k * 0.6180339887 ) * 4 );
  const spread = 1.42 + fr( k * 0.3775 ) * 0.5;
  const len = 46 + fr( k * 0.7548776662 ) * 10;
  const droop = 0.18 + fr( k * 0.4142135624 ) * 0.3;
  const hx = 60, hy = 96;                      // the hinge, where the stalk ends
  const parts: string[] = [];
  for ( let i = 0; i < n; i++ ) {
    const t = n === 1 ? 0.5 : i / ( n - 1 );
    const a = -Math.PI / 2 + ( t - 0.5 ) * spread;
    const edge = Math.abs( t - 0.5 ) * 2;
    const L = len * ( 1 - edge * 0.3 );
    const dr = droop * ( 0.4 + edge * 1.1 );
    const tipX = hx + Math.cos( a ) * L;
    const tipY = hy + Math.sin( a ) * L + dr * L * 0.55;
    const w = 3.4 - edge * 1.5;
    const nx = -Math.sin( a ), ny = Math.cos( a );
    const midX = hx + Math.cos( a ) * L * 0.55;
    const midY = hy + Math.sin( a ) * L * 0.55 + dr * L * 0.16;
    parts.push(
      `M${ f( hx ) },${ f( hy ) }`
      + ` C${ f( midX + nx * w ) },${ f( midY + ny * w ) } ${ f( tipX + nx * w * 0.45 ) },${ f( tipY + ny * w * 0.45 ) } ${ f( tipX ) },${ f( tipY ) }`
      + ` C${ f( tipX - nx * w * 0.45 ) },${ f( tipY - ny * w * 0.45 ) } ${ f( midX - nx * w ) },${ f( midY - ny * w ) } ${ f( hx ) },${ f( hy ) } Z`
    );
  }
  parts.push(
    `M${ f( hx - 1.9 ) },${ f( hy ) }`
    + ` C${ f( hx - 2.2 ) },${ f( hy + 9 ) } ${ f( hx - 1.6 ) },${ f( hy + 15 ) } ${ f( hx - 1.2 ) },${ f( hy + 20 ) }`
    + ` L${ f( hx + 1.2 ) },${ f( hy + 20 ) }`
    + ` C${ f( hx + 1.6 ) },${ f( hy + 15 ) } ${ f( hx + 2.2 ) },${ f( hy + 9 ) } ${ f( hx + 1.9 ) },${ f( hy ) } Z`
  );
  return parts.join( ' ' );
}

/** Eight fans: leaflet count, spread, length and droop all vary, so a scatter
 *  never shows the same outline twice nearby. Anchored at the HINGE (60,96), not
 *  the box centre, so a consumer rotates the leaf about its stalk. */
export const FAN_FRONDS: string[] = [ 3, 4, 5, 6, 7, 8, 9, 10 ].map( fanFrond );
